/**
 * NewForm design-system Alert.
 *
 * Tinted info / status block — used for inline notices, batch results,
 * non-blocking errors, etc. Renders the framework UI kit's `.a` block 1:1:
 * a 1px-bordered, 8px-rounded card with a colored 8px dot at the start,
 * a bold sentence-case title, and a body line below.
 *
 *   import {
 *     Alert,
 *     AlertTitle,
 *     AlertDescription,
 *   } from "@/components/ui/design-system/alert";
 *
 *   <Alert variant="warn">
 *     <AlertTitle>Batch results</AlertTitle>
 *     <AlertDescription>
 *       Batch terminated — no ad reached the binomial p-value threshold.
 *     </AlertDescription>
 *   </Alert>
 *
 * Each variant pairs a semantic tone foreground and background in both themes.
 *
 * Variants (matching the spec preview at
 * packages/nf-design-system/preview/components-alerts.html):
 *
 *   info — sky tint (status: neutral, sync-in-progress, heads-up)
 *   ok   — green tint (status: success)
 *   warn — amber tint (status: pending decision, terminated, capped)
 *   err  — red tint (status: hard failure / error)
 *
 * For multi-line descriptions, just nest paragraphs / lists inside
 * `<AlertDescription>` — the wrapper applies the right leading + spacing.
 * Sentence case for titles per DS microcopy rules; no Title Case.
 */

import { cva, type VariantProps } from "class-variance-authority";
import type { HTMLAttributes } from "react";
import { cn } from "@/lib/utils";

const alertVariants = cva(
  "flex items-start gap-nf-ds-2 rounded-nf-ds-md border px-nf-ds-3 py-nf-ds-3 font-nf-ds-sans text-[13px] leading-[1.5]",
  {
    variants: {
      variant: {
        info: "border-nf-ds-tone-info-fg/25 bg-nf-ds-tone-info-bg text-nf-ds-tone-info-fg",
        ok: "border-nf-ds-tone-ok-fg/25 bg-nf-ds-tone-ok-bg text-nf-ds-tone-ok-fg",
        warn: "border-nf-ds-tone-warn-fg/25 bg-nf-ds-tone-warn-bg text-nf-ds-tone-warn-fg",
        err: "border-nf-ds-tone-err-fg/25 bg-nf-ds-tone-err-bg text-nf-ds-tone-err-fg",
      },
    },
    defaultVariants: {
      variant: "info",
    },
  },
);

const dotVariants = cva("shrink-0 mt-[6px] h-2 w-2 rounded-full", {
  variants: {
    variant: {
      info: "bg-current",
      ok: "bg-current",
      warn: "bg-current",
      err: "bg-current",
    },
  },
  defaultVariants: {
    variant: "info",
  },
});

interface AlertProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {}

export function Alert({ className, variant, children, ...props }: AlertProps) {
  return (
    // biome-ignore lint/a11y/useSemanticElements: role=status announces live alerts; output/section lack equivalent live-region semantics
    <div
      role="status"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    >
      <span aria-hidden className={cn(dotVariants({ variant }))} />
      <div className="min-w-0 flex-1 space-y-nf-ds-1">{children}</div>
    </div>
  );
}

export function AlertTitle({
  className,
  ...props
}: HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p
      className={cn(
        "font-nf-ds-sans text-[13px] font-[600] leading-[1.4]",
        className,
      )}
      {...props}
    />
  );
}

export function AlertDescription({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "font-nf-ds-sans text-[13px] leading-[1.5] text-current/85 [&_p]:m-0",
        className,
      )}
      {...props}
    />
  );
}
